import { AppDispatch } from "./redux-store";
import * as Sentry from "@sentry/react";
import { cargoViewsUpdated } from "./ducks/data/cargo-views";
import { viewModelsUpdated } from "./ducks/data/route-views";
import { contactsReceived } from "./ducks/data/economics/customer-contacts";
import { clientsRemoved } from "./ducks/data/clients";
import { CustomerContact } from "./ducks/data/economics/customer-contacts/types";

// Socket messages that can arrive in bursts (a public-API import, the nightly
// schedule run, an e-conomic contact sync, a finance-system customer sync
// deactivating clients). Applying each one as its own
// store update commits React once per message, and enough of those
// back-to-back trips React's "Maximum update depth exceeded" (DORA-385). So
// they're buffered and applied together every FLUSH_DELAY_MS. Route and cargo
// views are pushed raw: their batch actions decode them, applying the valid
// ones and throwing for the rest.
export type SocketUpdate =
  | { kind: "cargo-view"; value: unknown }
  | { kind: "route-view"; value: unknown }
  | {
      kind: "customer-contact";
      value: { contact: CustomerContact; created: boolean };
    }
  | { kind: "client-removed"; value: string };

type Run =
  | { kind: "cargo-view"; values: unknown[] }
  | { kind: "route-view"; values: unknown[] }
  | {
      kind: "customer-contact";
      values: { contact: CustomerContact; created: boolean }[];
    }
  | { kind: "client-removed"; values: string[] };

export const FLUSH_DELAY_MS = 100;

// Merges consecutive updates of the same kind into one run, but keeps the
// arrival order between kinds: a route-view update carries cargo views too, so
// reordering it against a cargo-view update could apply a stale cargo last.
export const groupConsecutive = (updates: SocketUpdate[]): Run[] => {
  const runs: Run[] = [];
  for (const update of updates) {
    const last = runs[runs.length - 1];
    if (last && last.kind === update.kind) {
      (last.values as unknown[]).push(update.value);
    } else {
      runs.push({ kind: update.kind, values: [update.value] } as Run);
    }
  }
  return runs;
};

const dispatchRun = (dispatch: AppDispatch, run: Run) => {
  switch (run.kind) {
    case "cargo-view":
      dispatch(cargoViewsUpdated(run.values));
      return;
    case "route-view":
      dispatch(viewModelsUpdated(run.values));
      return;
    case "customer-contact":
      dispatch(contactsReceived(run.values));
      return;
    case "client-removed":
      dispatch(clientsRemoved({ ids: run.values }));
      return;
  }
};

export const createSocketUpdateBuffer = (
  dispatch: AppDispatch,
  delayMs = FLUSH_DELAY_MS
) => {
  let pending: SocketUpdate[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;

  // All of a flush's dispatches run in the same task, so React renders them
  // as one commit.
  const flush = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    const updates = pending;
    pending = [];
    for (const run of groupConsecutive(updates)) {
      try {
        dispatchRun(dispatch, run);
      } catch (err) {
        // Keep applying the rest of the flush; a bad message shouldn't drop
        // the updates queued behind it.
        Sentry.captureException(err, { extra: { kind: run.kind } });
      }
    }
  };

  const push = (update: SocketUpdate) => {
    pending.push(update);
    if (!timer) {
      timer = setTimeout(flush, delayMs);
    }
  };

  return { push, flush };
};
